import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

// ── Types ────────────────────────────────────────────────────────────────────

type PopupPriority = "low" | "normal" | "high" | "critical";

interface PopupRequest {
  id: string;
  priority: PopupPriority;
  render: () => ReactNode;
}

interface PopupManagerCtx {
  /** Request to show a popup. Returns a cleanup function. */
  request: (req: PopupRequest) => () => void;
  /** Dismiss the currently visible popup by id. */
  dismiss: (id: string) => void;
  /** True when any popup is currently visible. */
  isActive: boolean;
  /** The id of the currently visible popup (null if none). */
  activeId: string | null;
}

const PRIORITY_WEIGHT: Record<PopupPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  critical: 3,
};

// ── Context ──────────────────────────────────────────────────────────────────

const PopupManagerContext = createContext<PopupManagerCtx | null>(null);

export function usePopupManager(): PopupManagerCtx {
  const ctx = useContext(PopupManagerContext);
  if (!ctx) {
    throw new Error("usePopupManager must be used inside <PopupManagerProvider>");
  }
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function PopupManagerProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<PopupRequest[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dismissedRef = useRef<Set<string>>(new Set());

  // Pick the highest-priority queued item that hasn't been dismissed.
  const activePopup =
    queue.length > 0
      ? [...queue]
          .filter((p) => !dismissedRef.current.has(p.id))
          .sort((a, b) => PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority])[0] ?? null
      : null;

  // Sync activeId state so consumers can read it.
  useEffect(() => {
    setActiveId(activePopup?.id ?? null);
  }, [activePopup?.id]);

  const request = useCallback((req: PopupRequest) => {
    // Deduplicate by id.
    setQueue((prev) => {
      if (prev.some((p) => p.id === req.id)) return prev;
      return [...prev, req];
    });

    // Return cleanup function that removes from queue.
    return () => {
      setQueue((prev) => prev.filter((p) => p.id !== req.id));
      dismissedRef.current.delete(req.id);
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    dismissedRef.current.add(id);
    setQueue((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return (
    <PopupManagerContext.Provider
      value={{ request, dismiss, isActive: activeId !== null, activeId }}
    >
      {children}

      {/* Render the active popup (if any). */}
      {activePopup ? activePopup.render() : null}
    </PopupManagerContext.Provider>
  );
}
